/**
 * CSV parser for the import wizard. Handles quoted fields, commas and line breaks inside quotes,
 * doubled quotes ("") as an escaped quote, CRLF / LF / CR line endings and a UTF-8 BOM. Blank lines are skipped.
 * Delimiter is detected from the first line (comma, semicolon or tab).
 */

const DELIMITERS = [',', ';', '\t']

/** Count each candidate delimiter on the first line, ignoring anything inside quotes. */
export const detectDelimiter = text => {
    const counts = { ',': 0, ';': 0, '\t': 0 }
    let inQuotes = false
    for (let i = 0; i < text.length; i += 1) {
        const c = text[i]
        if (c === '"') inQuotes = !inQuotes
        else if (!inQuotes && (c === '\n' || c === '\r')) break
        else if (!inQuotes && counts[c] !== undefined) counts[c] += 1
    }
    let best = ','
    DELIMITERS.forEach(d => {
        if (counts[d] > counts[best]) best = d
    })
    return best
}

/**
 * @param {string} input
 * @param {{ delimiter?: string }} [options]
 * @returns {{ rows: string[][], delimiter: string, unterminatedQuote: boolean }}
 */
export const parseCsv = (input, options = {}) => {
    let text = String(input ?? '')
    if (text.charCodeAt(0) === 0xfeff) text = text.slice(1)
    const delimiter = options.delimiter || detectDelimiter(text)

    const rows = []
    let row = []
    let field = ''
    let inQuotes = false
    let sawContent = false // anything on the current line, including an empty quoted field

    const endField = () => {
        row.push(field)
        field = ''
    }
    const endRow = () => {
        endField()
        if (row.some(cell => cell.trim() !== '')) rows.push(row)
        row = []
        sawContent = false
    }

    for (let i = 0; i < text.length; i += 1) {
        const c = text[i]
        if (inQuotes) {
            if (c === '"') {
                if (text[i + 1] === '"') {
                    field += '"'
                    i += 1
                } else {
                    inQuotes = false
                }
            } else {
                field += c
            }
        } else if (c === '"' && field === '') {
            inQuotes = true
            sawContent = true
        } else if (c === delimiter) {
            endField()
            sawContent = true
        } else if (c === '\r' || c === '\n') {
            if (c === '\r' && text[i + 1] === '\n') i += 1
            endRow()
        } else {
            field += c
            sawContent = true
        }
    }
    if (sawContent || field !== '' || row.length > 0) endRow()

    return { rows, delimiter, unterminatedQuote: inQuotes }
}
